import { useRef, useEffect, useState, useCallback, memo } from "react";

/**
 * PlacesAutocomplete using the new PlaceAutocompleteElement API
 * Replaces the deprecated google.maps.places.Autocomplete widget
 */
const PlacesAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Enter location...",
  className = "",
  type = "city",
  "data-testid": testId,
}) => {
  const containerRef = useRef(null);
  const elementRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [displayValue, setDisplayValue] = useState(value || "");
  const ignoreNextSyncRef = useRef(false);
  const [useFallback, setUseFallback] = useState(false);
  const inputRef = useRef(null);
  const fallbackAutocompleteRef = useRef(null);

  // Sync from external value
  useEffect(() => {
    if (ignoreNextSyncRef.current) {
      ignoreNextSyncRef.current = false;
      return;
    }
    setDisplayValue(value || "");
  }, [value]);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google?.maps?.places) {
        setIsLoaded(true);
        return true;
      }
      return false;
    };
    if (checkGoogleMaps()) return;
    const interval = setInterval(() => {
      if (checkGoogleMaps()) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Parse place data from address components
  const parsePlaceData = useCallback((addressComponents, placeName) => {
    let city = "";
    let country = "";
    let state = "";

    for (const component of addressComponents) {
      const types = component.types;
      if (types.includes("locality")) {
        city = component.longText || component.long_name || "";
      } else if (types.includes("administrative_area_level_1")) {
        state = component.shortText || component.short_name || "";
      } else if (types.includes("country")) {
        country = component.longText || component.long_name || "";
      }
    }

    if (type === "country") {
      const selectedCountry = country || placeName || "";
      ignoreNextSyncRef.current = true;
      setDisplayValue(selectedCountry);
      onChange({ target: { value: selectedCountry } });
      onPlaceSelect?.({ country: selectedCountry });
    } else {
      const selectedCity = city || placeName || "";
      let formattedValue = selectedCity;
      if (state && country === "United States") {
        formattedValue = `${selectedCity}, ${state}`;
      } else if (state) {
        formattedValue = `${selectedCity}, ${state}, ${country}`;
      } else if (country) {
        formattedValue = `${selectedCity}, ${country}`;
      }
      ignoreNextSyncRef.current = true;
      setDisplayValue(formattedValue);
      onChange({ target: { value: formattedValue } });
      onPlaceSelect?.({ city: formattedValue, country, state, displayValue: formattedValue });
    }
  }, [type, onChange, onPlaceSelect]);

  // Try new API first, fall back to legacy if needed
  useEffect(() => {
    if (!isLoaded || !containerRef.current) return;
    if (elementRef.current) return;

    // Try to use the new PlaceAutocompleteElement
    try {
      if (window.google.maps.places.PlaceAutocompleteElement) {
        const options = {};
        if (type === "country") {
          options.includedPrimaryTypes = ["country"];
        } else if (type === "city") {
          options.includedPrimaryTypes = ["(cities)"];
        }

        const element = new window.google.maps.places.PlaceAutocompleteElement(options);
        elementRef.current = element;

        // Style the element's input via attributes
        element.setAttribute("placeholder", placeholder);
        element.style.cssText = "width:100%;display:block;";

        // Handle place selection with new API
        const handleSelect = async (event) => {
          try {
            let place;
            if (event.placePrediction) {
              place = event.placePrediction.toPlace();
            } else if (event.place) {
              place = event.place;
            }
            if (place && place.fetchFields) {
              await place.fetchFields({ fields: ["addressComponents", "displayName", "formattedAddress", "location"] });
              const components = place.addressComponents || [];
              parsePlaceData(components, place.displayName || "");
            }
          } catch (err) {
          }
        };

        // Try both event names for compatibility
        element.addEventListener("gmp-select", handleSelect);
        element.addEventListener("gmp-placeselect", handleSelect);

        // Clear and append
        containerRef.current.replaceChildren();
        containerRef.current.appendChild(element);
        return;
      }
    } catch (err) {
    }

    // Fallback: use legacy Autocomplete if new API unavailable
    setUseFallback(true);
  }, [isLoaded, type, placeholder, parsePlaceData]);

  // Legacy fallback setup
  useEffect(() => {
    if (!useFallback || !isLoaded || !inputRef.current || fallbackAutocompleteRef.current) return;

    const options = {
      fields: ["address_components", "formatted_address", "geometry", "name"],
    };
    if (type === "country") options.types = ["country"];
    else if (type === "city") options.types = ["(cities)"];
    else options.types = ["geocode", "establishment"];

    try {
      fallbackAutocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);
      fallbackAutocompleteRef.current.addListener("place_changed", () => {
        const place = fallbackAutocompleteRef.current.getPlace();
        if (!place.address_components) return;
        parsePlaceData(place.address_components, place.name);
      });
    } catch (error) {
    }
  }, [useFallback, isLoaded, type, parsePlaceData]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    onChange(e);
  }, [onChange]);

  // If using fallback (legacy), render our own input
  if (useFallback) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
        autoComplete="off"
        data-testid={testId}
      />
    );
  }

  // New API: render the container for PlaceAutocompleteElement
  return (
    <div
      ref={containerRef}
      className={`places-autocomplete-container ${className}`}
      data-testid={testId}
    />
  );
};

PlacesAutocomplete.displayName = "PlacesAutocomplete";

export default memo(PlacesAutocomplete);
